import { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'equasion-lib';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import { eqProps } from '../eqProps';
import type { VerifyLotLike, VerifyMode } from '../verifyScanQueue';

type VerifyLotChooserProps = {
  mode: VerifyMode;
  productName: string;
  /**
   * The lots to choose from — units available (Add) / verified (Remove), net of what is still
   * saving (verifyLotOptions) — in the server's order, its suggested row first.
   */
  options: readonly VerifyLotLike[];
  /** Units the line still needs (Add) or still holds (Remove), counting what is still saving. */
  remaining: number;
  /** The row it opens on (verifyLotPreselect). */
  initialBalanceId: string | null;
  /**
   * The units the reads asked for: 1 per scan, or the typed quantity — and growing with every
   * further read of the line while the chooser is open (they wait here for their lot).
   */
  quantity: number;
  onConfirm: (balanceId: string, quantity: number) => void;
  onClose: () => void;
};

const isWholeNumber = (value: string) => /^\d+$/.test(value.trim()) && Number(value) >= 1;

const lotName = (lot: VerifyLotLike) =>
  lot.lot?.trim() ? `Lot ${lot.lot.trim()}` : 'No lot';

/**
 * Why a quantity cannot be sent from `lot` (which holds `has`), or null — nothing for an empty
 * field, whose button is simply disabled. The instruction leads: an old handheld shows only the
 * first ~50 characters of a line.
 */
const quantityProblem = (
  mode: VerifyMode,
  value: string,
  lot: VerifyLotLike,
  has: number,
  remaining: number
): string | null => {
  if (!isWholeNumber(value)) {
    return value.trim() ? 'Enter a whole number of units (1 or more).' : null;
  }
  const count = Number(value.trim());
  if (count > remaining) {
    return mode === 'add'
      ? `Enter ${remaining} or fewer — only ${remaining} more unit(s) are needed.`
      : `Enter ${remaining} or fewer — only ${remaining} unit(s) are verified.`;
  }
  if (count > has) {
    const where = lot.zone?.name ? ` in ${lot.zone.name}` : '';
    return mode === 'add'
      ? `Enter ${has} or fewer — ${lotName(lot)}${where} has only ${has} available.`
      : `Enter ${has} or fewer — only ${has} of ${lotName(lot)} are verified.`;
  }
  return null;
};

/**
 * Which lot a BULK / UPC read is — or, in Remove mode, which lot goes back to stock — when the line
 * can use more than one (verifyScanQueue rule 7). Each row is one stock balance: lot, expiration,
 * zone, and what it holds. It opens on the scanned lot or the server's suggested one with the read's
 * quantity, so the common case is one tap. A mixed box is one confirmation per lot (18 × ABC, then
 * 6 × XYZ): the page keeps the chooser open until the line is done, and after each confirmation the
 * quantity offers what is left of the read (else 1), never more than the line needs or the lot holds
 * — a row that runs out drops off the list and the next one is selected. Further reads of the line
 * while it is open add to the quantity waiting for a lot (nothing scanned is dropped unheard). Every quantity is checked
 * here, before it is sent: these confirmations skip the triage's re-read guards on purpose.
 * Rendered in a bottom sheet for a scan, inside the entry form for a typed code.
 */
function VerifyLotChooser({
  mode,
  productName,
  options,
  remaining,
  initialBalanceId,
  quantity,
  onConfirm,
  onClose
}: VerifyLotChooserProps) {
  const [selectedId, setSelectedId] = useState(initialBalanceId);
  // Null: the default below. Typed: kept across a change of row.
  const [typed, setTyped] = useState<string | null>(null);
  // What is left of the read's own quantity to attribute to a lot.
  const [left, setLeft] = useState(quantity);
  const [note, setNote] = useState<string | null>(null);

  // A further read of the line: its units join what is waiting for a lot, and the quantity field
  // offers them all again (a value the operator typed gives way to it).
  const quantityRef = useRef(quantity);
  useEffect(() => {
    const added = quantity - quantityRef.current;
    quantityRef.current = quantity;
    if (added <= 0) return;
    setLeft((n) => n + added);
    setTyped(null);
    setNote(`+${added} scanned — choose the lot and confirm.`);
  }, [quantity]);

  const units = (lot: VerifyLotLike) => (mode === 'add' ? lot.available : lot.verified);
  const selected =
    options.find((lot) => lot.balanceId === selectedId) ?? options[0] ?? null;
  const most = selected ? Math.min(units(selected), remaining) : 0;
  const value = typed ?? String(Math.max(1, Math.min(left > 0 ? left : 1, most)));
  const count = isWholeNumber(value) ? Number(value.trim()) : null;
  const problem = selected
    ? quantityProblem(mode, value, selected, units(selected), remaining)
    : null;
  const ready = !!selected && count !== null && !problem;
  const verb = mode === 'remove' ? 'Remove' : 'Add';

  const confirm = () => {
    if (!ready || !selected || count === null) return;
    onConfirm(selected.balanceId, count);
    setLeft((n) => Math.max(0, n - count));
    setTyped(null);
    setNote(`${count} × ${lotName(selected)} sent.`);
  };

  return (
    <div className="verify-lots">
      <p className="verify-lots__title">
        {mode === 'remove'
          ? 'Choose the lot to take off, then confirm'
          : 'Choose the lot, then confirm the quantity'}
      </p>
      <p className="verify-lots__text">
        {productName} — {remaining} {mode === 'remove' ? 'verified' : 'more needed'}
      </p>
      <div className="verify-lots__options" role="radiogroup" aria-label="Lot">
        {options.map((lot) => {
          const checked = lot.balanceId === selected?.balanceId;
          const expiration = formatDateOnly(lot.expiration);
          return (
            <button
              key={lot.balanceId}
              type="button"
              role="radio"
              aria-checked={checked}
              className={['verify-lots__option', checked && 'active']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setSelectedId(lot.balanceId)}
            >
              <span className="verify-lots__lot">
                {lotName(lot)}
                {lot.suggested && <small>Suggested</small>}
              </span>
              <span className="verify-lots__meta">
                {[expiration && `Exp ${expiration}`, lot.zone?.name]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              <span className="verify-lots__units">
                {units(lot)} {mode === 'remove' ? 'verified' : 'available'}
              </span>
            </button>
          );
        })}
      </div>
      <Input
        {...eqProps({
          form: false,
          label: 'Quantity',
          value,
          onChange: ({ value: next }: { value: string }) => setTyped(next)
        })}
      />
      {!!problem && <p className="verify-lots__error">{problem}</p>}
      <div className="verify-lots__actions">
        <Button
          type={mode === 'remove' ? 'danger' : 'primary'}
          fullWidth
          disabled={!ready}
          {...eqProps({ onClick: confirm })}
        >
          {verb} {count ?? ''} unit(s)
        </Button>
        <Button type="secondary" fullWidth {...eqProps({ onClick: onClose })}>
          Close
        </Button>
      </div>
      {!!note && (
        <p className="verify-lots__sent" role="status">
          {note}
        </p>
      )}
    </div>
  );
}

export default VerifyLotChooser;
